import { IAcquisitionVariableFullData } from "../../../../../types";
import { getUID } from "../../../../../system/utils";
import { comparisonType, ICondition } from "../../../types";
import TableContainer from "../../../../../components/tableContainer";
import {
  Autocomplete,
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow, TextField,
  Tooltip
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../../../configuration/configuration";
import StyledTableRow from "../../../../../components/styledTableRow";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { ReactElement } from "react";

interface IProps {
  conditions: ICondition[];
  setConditions: (conditions: ICondition[]) => void;
  machines: string[];
  machineVariables: Record<string, IAcquisitionVariableFullData[]>;
}

const maxVar = 6;

interface IVariableRowProps {
  index: number;
  machines: string[];
  machineVariables: Record<string, IAcquisitionVariableFullData[]>;
  data: ICondition;
  setData: (data: ICondition) => void;
  onRemoveVariable: (index: number) => void;
}

const VariableRow = ({ index, machineVariables, machines, data, setData, onRemoveVariable }: IVariableRowProps): ReactElement => {
  const found = machineVariables[data.machine]?.find(x => x.code === data.value) ?? null;

  const updateItemsSeries = (field: keyof ICondition, value: any) => {

    if (field === "machine"){
      data.machine = value;
      data.value = "";
    } else if(field === "comparison") {
      data[field] = value;
    } else if (field === "severity"){
      data[field] = value;
    } else if(field === "value") {
      data[field] = value;
    } else if(field === "type") {
      data[field] = value;
    }

    setData(data);
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <Select
          sx={{ width: 100 }}
          variant="standard"
          value={data.type}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) => updateItemsSeries("type", event.target.value as string)}>
          <MenuItem value={"TAG"}>TAG</MenuItem>
          <MenuItem value={"VALUE"}>VALUE</MenuItem>
        </Select>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            sx={{ width: 100 }}
            disabled={data.type !== "TAG"}
            variant="standard"
            value={data.machine}
            displayEmpty={true}
            onChange={(event: SelectChangeEvent) => updateItemsSeries("machine", event.target.value as string)}>
            { machines.map(machine => {
              return <MenuItem key={`unit-${index}-${machine}`} value={machine}>{machine}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        {data.type === "TAG" &&
          <Autocomplete
            sx={{ width: 300 }}
            options={machineVariables[data.machine]}
            getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
            value={found}
            openOnFocus={true}
            onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
              updateItemsSeries("value", newValue?.code);
            }}
            renderInput={(params) =>
              <TextField {...params} variant="standard" color={"primary"}/>
            }
          />
        }

        {data.type === "VALUE" &&
          <TextField
            sx={{ width: 300 }}
            variant="standard"
            size="small"
            value={(data.value ?? 0) as number}
            type="number"
            onChange={(event) => updateItemsSeries("value", parseFloat(event.target.value as string))}
          />
        }
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.comparison}
            displayEmpty={true}
            onChange={(event) => updateItemsSeries("comparison", event.target.value as comparisonType)}>
            <MenuItem value={">"}>&gt;</MenuItem>
            <MenuItem value={">="}>&gt;=</MenuItem>
            <MenuItem value={"<"}>&lt;</MenuItem>
            <MenuItem value={"<="}>&lt;=</MenuItem>
            <MenuItem value={"<>"}>&lt;&gt;</MenuItem>
            <MenuItem value={"="}>=</MenuItem>
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.severity}
            displayEmpty={true}
            onChange={(event) => updateItemsSeries("severity", event.target.value as string)}>
            <MenuItem value={"error"}>ERROR</MenuItem>
            <MenuItem value={"warning"}>WARNING</MenuItem>
            <MenuItem value={"info"}>INFO</MenuItem>
            <MenuItem value={"none"}>NONE</MenuItem>
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }} onClick={() => onRemoveVariable(index)}>
          <Tooltip title={"Remove variable"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>
    </StyledTableRow>
  );
};

const ConditionConfig = ({ conditions, setConditions, machineVariables, machines }: IProps): ReactElement => {

  const addCondition = () => {
    setConditions([ ...conditions, {
      hash: getUID(),
      machine: machines[0],
      type: "VALUE",
      comparison: "=",
      value: 0,
      severity: "error"
    }]);
  };

  const onRemoveVariable = (index: number) => {
    conditions.splice(index, 1);
    setConditions([ ...conditions ]);
  };

  return (
    <TableContainer style={{ maxHeight: 570, width: 900 }}>
      <Table stickyHeader={true} size={"small"} >
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
              <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }}
                onClick={addCondition} disabled={conditions.length >= maxVar || machines.length === 0}>
                <Tooltip title={"Add variable"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100, maxWidth: 100 }}>Type</TableCell>
            <TableCell className="table-header" style={{ minWidth: 100, maxWidth: 100 }}>Machine</TableCell>
            <TableCell className="table-header" style={{ minWidth: 180, maxWidth: 250 }}>
              Value - {conditions.length} / {maxVar}
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Condition</TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Status</TableCell>
            <TableCell className="table-header"/>
          </TableRow>
        </TableHead>
        <TableBody>
          { conditions.map((row, index) => {
            return (
              <VariableRow key={`series-row-${index}`}
                onRemoveVariable={onRemoveVariable}
                machineVariables={machineVariables}
                machines={machines} data={row} index={index} setData={() => setConditions([ ...conditions ])} />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConditionConfig;